import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, FaFileAlt, FaUserAlt, FaBoxOpen, 
  FaHistory, FaPrint, FaDownload, FaTimesCircle, 
  FaEnvelope, FaPhone, FaIdCard
} from 'react-icons/fa';
import supabase from '../../supabaseClient';
import ItensPedidoComponent from './ItensPedidoComponent';
import '../../styles/order-details.css';

const OrderDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [expositor, setExpositor] = useState(null);
  const [evento, setEvento] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [updatedItems, setUpdatedItems] = useState(null);
  const [itemsChanged, setItemsChanged] = useState(false);

  // Verificar se o usuário é admin
  useEffect(() => {
    const checkAdminRole = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Verificar se o usuário é admin na tabela de administradores
          const { data: adminData, error } = await supabase
            .from('caex_administradores')
            .select('*')
            .eq('user_id', user.id)
            .eq('ativo', true)
            .single();
          
          if (!error && adminData) {
            setIsAdmin(true);
          }
        }
      } catch (error) {
        console.error('Erro ao verificar permissões de admin:', error);
      }
    };

    checkAdminRole();
  }, []);

  // Carregar dados do pedido
  useEffect(() => {
    const fetchOrderData = async () => {
      setLoading(true);
      try {
        // Buscar detalhes do pedido
        const { data: orderData, error: orderError } = await supabase
          .from('caex_pedidos')
          .select('*')
          .eq('id', id)
          .single();
        
        if (orderError) throw orderError;
        if (!orderData) throw new Error('Pedido não encontrado');
        
        setOrder(orderData);
        
        // Buscar itens do pedido
        const { data: itemsData, error: itemsError } = await supabase
          .from('caex_pedidos_itens')
          .select('*, produto:produto_id(nome, descricao, categoria)')
          .eq('pedido_id', id);
        
        if (itemsError) throw itemsError;
        
        // Formatar itens para exibição
        const formattedItems = itemsData.map(item => ({
          id: item.produto_id,
          name: item.produto?.nome || `Produto ID: ${item.produto_id}`,
          description: item.produto?.descricao || '',
          category: item.produto?.categoria || '',
          price: item.preco_original || item.preco_unitario,
          customPrice: item.preco_unitario !== item.preco_original ? item.preco_unitario : null,
          quantity: item.quantidade,
          discount: item.desconto_aplicado || 0,
          discountType: item.tipo_desconto || 'value',
          originalPrice: item.preco_original
        }));
        
        setOrderItems(formattedItems);
        
        // Buscar dados do expositor
        if (orderData.expositor_id) {
          const { data: expositorData, error: expositorError } = await supabase
            .from('caex_expositores')
            .select('*')
            .eq('id', orderData.expositor_id)
            .single();
          
          if (!expositorError && expositorData) {
            setExpositor(expositorData);
          }
        }
        
        // Buscar dados do evento
        if (orderData.evento_id) {
          const { data: eventoData, error: eventoError } = await supabase
            .from('caex_eventos')
            .select('*')
            .eq('id', orderData.evento_id)
            .single();
          
          if (!eventoError && eventoData) {
            setEvento(eventoData);
          }
        }
        
        // Simular histórico de alterações (em produção, isso viria do banco de dados)
        const mockHistory = [
          {
            id: 1,
            action: 'Pedido criado',
            user: 'João Silva',
            date: new Date(orderData.created_at || Date.now()).toISOString(),
            description: 'Pedido enviado pelo expositor'
          },
          {
            id: 2,
            action: 'Status alterado',
            user: 'Administrador',
            date: new Date(orderData.updated_at || Date.now()).toISOString(),
            description: `Status alterado para "${orderData.status}"`
          }
        ];
        
        setHistoryItems(mockHistory);
        
      } catch (error) {
        console.error('Erro ao carregar dados do pedido:', error);
        setError(error.message || 'Erro ao carregar o pedido');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchOrderData();
    }
  }, [id]);

  // Atualizar items do pedido (apenas admin)
  const handleUpdateItems = (items) => {
    if (!isAdmin) return;
    
    // Verificar se houve alterações nos itens
    const itemsChanged = JSON.stringify(items) !== JSON.stringify(orderItems);
    setItemsChanged(itemsChanged);
    
    if (itemsChanged) {
      setUpdatedItems(items);
    } else {
      setUpdatedItems(null);
    }
  };

  // Salvar alterações nos itens
  const saveItemChanges = async () => {
    if (!updatedItems || !isAdmin) return;
    
    try {
      setLoading(true);
      
      // Preparar itens atualizados para o banco de dados
      const updatedDbItems = updatedItems.map(item => {
        const finalPrice = calculateItemFinalPrice(item);
        return {
          pedido_id: parseInt(id),
          produto_id: item.id,
          quantidade: item.quantity,
          preco_unitario: finalPrice,
          preco_total: finalPrice * item.quantity,
          preco_original: item.originalPrice || item.price,
          desconto_aplicado: item.discount || 0,
          tipo_desconto: item.discountType || 'value'
        };
      });
      
      // Primeiro, excluir os itens antigos
      const { error: deleteError } = await supabase
        .from('caex_pedidos_itens')
        .delete()
        .eq('pedido_id', id);
      
      if (deleteError) throw deleteError;
      
      // Inserir os novos itens
      const { error: insertError } = await supabase
        .from('caex_pedidos_itens')
        .insert(updatedDbItems);
      
      if (insertError) throw insertError;
      
      // Calcular novo valor total
      const newTotal = updatedItems.reduce((total, item) => {
        const finalPrice = calculateItemFinalPrice(item);
        return total + (finalPrice * item.quantity);
      }, 0);
      
      // Atualizar o valor total do pedido
      const { error: updateError } = await supabase
        .from('caex_pedidos')
        .update({ 
          valor_total: newTotal,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
      
      if (updateError) throw updateError;
      
      // Atualizar estado local
      setOrder({
        ...order,
        valor_total: newTotal,
        updated_at: new Date().toISOString()
      });
      
      setOrderItems(updatedItems);
      setItemsChanged(false);
      setUpdatedItems(null);
      
      // Adicionar entrada ao histórico
      const newHistoryItem = {
        id: historyItems.length + 1,
        action: 'Itens alterados',
        user: 'Administrador',
        date: new Date().toISOString(),
        description: 'Preços e/ou quantidades alterados pelo administrador'
      };
      
      setHistoryItems([...historyItems, newHistoryItem]);
      
      alert('Alterações salvas com sucesso!');
      
    } catch (error) {
      console.error('Erro ao salvar alterações:', error);
      alert('Erro ao salvar alterações. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Função auxiliar para calcular preço final do item
  const calculateItemFinalPrice = (item) => {
    const basePrice = item.customPrice || item.price;
    const discount = item.discount || 0;
    
    if (discount === 0) return basePrice;
    
    if (item.discountType === 'percentage') {
      return basePrice * (1 - discount / 100);
    } else {
      return Math.max(0, basePrice - discount);
    }
  };

  // Format currency values
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Format date values
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Renderizar badge de status
  const renderStatusBadge = (status) => {
    let badgeClass = 'pending';
    let badgeText = 'Pendente';
    
    switch (status) {
      case 'aprovado':
        badgeClass = 'approved';
        badgeText = 'Aprovado';
        break;
      case 'cancelado':
        badgeClass = 'canceled';
        badgeText = 'Cancelado';
        break;
      case 'pago':
        badgeClass = 'paid';
        badgeText = 'Pago';
        break;
      default:
        badgeClass = 'pending';
        badgeText = 'Pendente';
    }
    
    return (
      <span className={`order-status-badge ${badgeClass}`}>
        {badgeText}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div>
          <div className="loading-spinner"></div>
          <p className="loading-text">Carregando detalhes do pedido...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-alert">
          <span className="error-icon">⚠️</span>
          <p className="error-message">{error}</p>
        </div>
        <button 
          className="error-back-btn"
          onClick={() => navigate('/orders')}
        >
          <FaArrowLeft /> Voltar para Lista de Pedidos
        </button>
      </div>
    );
  }

  return (
    <div className="order-details-container">
      <div className="order-details-hub">
        {/* Header */}
        <div className="order-details-header">
          <div className="order-details-title-section">
            <div className="order-details-title-left">
              <button 
                className="order-details-back-button"
                onClick={() => navigate('/orders')}
              >
                <FaArrowLeft />
              </button>
              <h1 className="order-details-main-title">
                Pedido #{id}
              </h1>
              <div className="order-details-status">
                {renderStatusBadge(order?.status)}
              </div>
              {isAdmin && (
                <Badge bg="warning" className="ms-2">
                  Modo Administrador
                </Badge>
              )}
            </div>
            
            <div className="order-details-actions">
              <button className="detail-action-btn print-btn">
                <FaPrint /> Imprimir
              </button>
              <button className="detail-action-btn download-btn">
                <FaDownload /> Baixar PDF
              </button>
              {order?.status !== 'cancelado' && (
                <button className="detail-action-btn cancel-btn">
                  <FaTimesCircle /> Cancelar
                </button>
              )}
              {isAdmin && itemsChanged && (
                <Button 
                  variant="success"
                  className="ms-2" 
                  onClick={saveItemChanges}
                  disabled={loading}
                >
                  Salvar Alterações
                </Button>
              )}
            </div>
          </div>
        </div>

        <Row>
          {/* Coluna Principal */}
          <Col lg={8}>
            {/* Itens do Pedido */}
            <ItensPedidoComponent 
              items={orderItems} 
              onUpdateItems={handleUpdateItems}
              readOnly={!isAdmin || order?.status === 'cancelado'}
            />
            
            {/* Histórico */}
            <Card className="order-details-card" style={{'--animation-delay': '0.2s'}}>
              <div className="details-card-header">
                <FaHistory className="details-card-icon" />
                <h5 className="details-card-title">Histórico do Pedido</h5>
              </div>
              <div className="details-card-body">
                <ul className="history-list">
                  {historyItems.map(item => (
                    <li key={item.id} className="history-item">
                      <div className="history-item-header">
                        <strong>{item.action}</strong>
                        <span className="history-time">{formatDate(item.date)}</span>
                      </div>
                      <div className="history-item-content">
                        <FaHistory className="history-icon" />
                        <div className="history-details">
                          <p className="history-description">{item.description}</p>
                          <p className="history-user">Por: {item.user}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          </Col>
          
          {/* Coluna Lateral */}
          <Col lg={4}>
            {/* Resumo do Pedido */}
            <Card className="order-details-card" style={{'--animation-delay': '0.1s'}}>
              <div className="details-card-header">
                <FaFileAlt className="details-card-icon" />
                <h5 className="details-card-title">Resumo do Pedido</h5>
              </div>
              <div className="details-card-body">
                <div className="order-info-row">
                  <div className="order-info-block">
                    <div className="order-info-label">Data do Pedido</div>
                    <div className="order-info-value">{formatDate(order?.created_at)}</div>
                  </div>
                  <div className="order-info-block">
                    <div className="order-info-label">Última Atualização</div>
                    <div className="order-info-value">{formatDate(order?.updated_at)}</div>
                  </div>
                </div>
                
                <div className="order-info-row">
                  <div className="order-info-block">
                    <div className="order-info-label">Status</div>
                    <div className="order-info-value">{renderStatusBadge(order?.status)}</div>
                  </div>
                  <div className="order-info-block">
                    <div className="order-info-label">Evento</div>
                    <div className="order-info-value">{evento?.nome || '-'}</div>
                  </div>
                </div>
                
                <div className="order-info-row">
                  <div className="order-info-block">
                    <div className="order-info-label">Itens</div>
                    <div className="order-info-value">
                      {orderItems.reduce((total, item) => total + item.quantity, 0)}
                    </div>
                  </div>
                  <div className="order-info-block">
                    <div className="order-info-label">Valor Total</div>
                    <div className="order-info-value order-info-total">
                      {formatCurrency(order?.valor_total)}
                    </div>
                  </div>
                </div>
                
                {order?.observacoes && (
                  <div className="order-info-row mt-3">
                    <div className="order-info-block">
                      <div className="order-info-label">Observações</div>
                      <div className="order-info-value" style={{ whiteSpace: 'pre-wrap' }}>
                        {order.observacoes}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
            
            {/* Dados do Expositor */}
            <Card className="order-details-card" style={{'--animation-delay': '0.3s'}}>
              <div className="details-card-header">
                <FaUserAlt className="details-card-icon" />
                <h5 className="details-card-title">Dados do Expositor</h5>
              </div>
              <div className="details-card-body">
                <div className="exhibitor-info">
                  <h6 className="exhibitor-name">{expositor?.nome_empresa || '-'}</h6>
                  
                  <p className="exhibitor-detail">
                    <FaIdCard className="exhibitor-icon" />
                    CNPJ: {expositor?.cnpj || '-'}
                  </p>
                  
                  <p className="exhibitor-detail">
                    <FaUserAlt className="exhibitor-icon" />
                    Responsável: {expositor?.responsavel || '-'}
                  </p>
                  
                  <p className="exhibitor-detail">
                    <FaEnvelope className="exhibitor-icon" />
                    Email: {expositor?.email || '-'}
                  </p>
                  
                  <p className="exhibitor-detail">
                    <FaPhone className="exhibitor-icon" />
                    Telefone: {expositor?.telefone || '-'}
                  </p>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default OrderDetailsPage;